import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Paper,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp,
  Receipt,
  PointOfSale,
  Warning,
  Add as AddIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  DragIndicator as DragIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { dashboardAPI, apiUtils } from '../services/api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Define available dashboard widgets
const WIDGET_TYPES = {
  STATS_CARDS: 'stats_cards',
  SALES_BY_CATEGORY: 'sales_by_category',
  SALES_BY_PAYMENT: 'sales_by_payment',
  TOP_PRODUCTS: 'top_products',
  FAST_MOVING_PRODUCTS: 'fast_moving_products',
  SALES_BY_STAFF: 'sales_by_staff',
  STOCK_WARNINGS: 'stock_warnings',
  RECENT_TRANSACTIONS: 'recent_transactions',
  RECENT_MOVEMENTS: 'recent_movements',
};

const WIDGET_CONFIGS = {
  [WIDGET_TYPES.STATS_CARDS]: {
    id: WIDGET_TYPES.STATS_CARDS,
    title: 'Business Statistics',
    description: 'Key business metrics and growth indicators',
    size: { xs: 12, sm: 12, md: 12, lg: 12 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.SALES_BY_CATEGORY]: {
    id: WIDGET_TYPES.SALES_BY_CATEGORY,
    title: 'Sales by Category',
    description: 'Today\'s sales breakdown by product category',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.SALES_BY_PAYMENT]: {
    id: WIDGET_TYPES.SALES_BY_PAYMENT,
    title: 'Sales by Payment Method',
    description: 'Today\'s sales breakdown by payment method',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.TOP_PRODUCTS]: {
    id: WIDGET_TYPES.TOP_PRODUCTS,
    title: 'Top Products Today',
    description: 'Best-selling products for today',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.FAST_MOVING_PRODUCTS]: {
    id: WIDGET_TYPES.FAST_MOVING_PRODUCTS,
    title: 'Fast Moving Products',
    description: 'Top performing products over the last 7 days',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.SALES_BY_STAFF]: {
    id: WIDGET_TYPES.SALES_BY_STAFF,
    title: 'Sales by Staff',
    description: 'Staff performance metrics for today',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.STOCK_WARNINGS]: {
    id: WIDGET_TYPES.STOCK_WARNINGS,
    title: 'Stock Warnings',
    description: 'Products with low stock levels',
    size: { xs: 12, sm: 12, md: 6, lg: 6 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.RECENT_TRANSACTIONS]: {
    id: WIDGET_TYPES.RECENT_TRANSACTIONS,
    title: 'Recent Transactions',
    description: 'Latest transaction activity',
    size: { xs: 12, sm: 12, md: 8, lg: 8 },
    defaultEnabled: true,
  },
  [WIDGET_TYPES.RECENT_MOVEMENTS]: {
    id: WIDGET_TYPES.RECENT_MOVEMENTS,
    title: 'Recent Product Movements',
    description: 'Latest inventory movements',
    size: { xs: 12, sm: 12, md: 4, lg: 4 },
    defaultEnabled: true,
  },
};

const STORAGE_KEY = 'dashboard_widget_settings';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [addWidgetAnchor, setAddWidgetAnchor] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Widget management state
  const [widgets, setWidgets] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  
  const [dashboardData, setDashboardData] = useState({
    todaysSales: 0,
    businessFigures: {},
    salesByCategory: [],
    salesByPaymentMethod: [],
    topProducts: [],
    fastMovingProducts: [],
    customersServed: 0,
    salesByStaff: [],
    recentTransactions: [],
    recentMovements: [],
    stockWarnings: [],
  });

  useEffect(() => {
    loadWidgetSettings();
    loadDashboardData();
  }, []);

  // Load widget settings from localStorage
  const loadWidgetSettings = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { widgets: savedWidgets } = JSON.parse(saved);
        setWidgets(savedWidgets);
        
        // Set available widgets (ones not currently added)
        const usedWidgetIds = savedWidgets.map(w => w.id);
        const available = Object.values(WIDGET_CONFIGS).filter(
          config => !usedWidgetIds.includes(config.id)
        );
        setAvailableWidgets(available);
      } else {
        // Use default configuration
        const defaultWidgets = Object.values(WIDGET_CONFIGS)
          .filter(config => config.defaultEnabled)
          .map((config, index) => ({
            ...config,
            order: index,
          }));
        setWidgets(defaultWidgets);
        setAvailableWidgets([]);
      }
    } catch (error) {
      console.error('Error loading widget settings:', error);
      // Fall back to default configuration
      const defaultWidgets = Object.values(WIDGET_CONFIGS)
        .filter(config => config.defaultEnabled)
        .map((config, index) => ({
          ...config,
          order: index,
        }));
      setWidgets(defaultWidgets);
      setAvailableWidgets([]);
    }
  };

  // Save widget settings to localStorage
  const saveWidgetSettings = (newWidgets) => {
    try {
      const settings = {
        widgets: newWidgets,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Error saving widget settings:', error);
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newWidgets = Array.from(widgets);
    const [reorderedWidget] = newWidgets.splice(result.source.index, 1);
    newWidgets.splice(result.destination.index, 0, reorderedWidget);

    // Update order values
    const updatedWidgets = newWidgets.map((widget, index) => ({
      ...widget,
      order: index,
    }));

    setWidgets(updatedWidgets);
    saveWidgetSettings(updatedWidgets);
  };

  // Add new widget
  const handleAddWidget = (widgetConfig) => {
    const newWidget = {
      ...widgetConfig,
      order: widgets.length,
    };
    
    const updatedWidgets = [...widgets, newWidget];
    setWidgets(updatedWidgets);
    saveWidgetSettings(updatedWidgets);

    // Remove from available widgets
    setAvailableWidgets(prev => prev.filter(w => w.id !== widgetConfig.id));
    setAddWidgetAnchor(null);
  };

  // Remove widget
  const handleRemoveWidget = (widgetId) => {
    const updatedWidgets = widgets.filter(w => w.id !== widgetId);
    setWidgets(updatedWidgets);
    saveWidgetSettings(updatedWidgets);

    // Add back to available widgets
    const removedWidget = WIDGET_CONFIGS[widgetId];
    if (removedWidget) {
      setAvailableWidgets(prev => [...prev, removedWidget]);
    }
  };

  // Reset to default layout
  const handleResetLayout = () => {
    const defaultWidgets = Object.values(WIDGET_CONFIGS)
      .filter(config => config.defaultEnabled)
      .map((config, index) => ({
        ...config,
        order: index,
      }));
    
    setWidgets(defaultWidgets);
    saveWidgetSettings(defaultWidgets);
    
    const usedWidgetIds = defaultWidgets.map(w => w.id);
    const available = Object.values(WIDGET_CONFIGS).filter(
      config => !usedWidgetIds.includes(config.id)
    );
    setAvailableWidgets(available);
  };

  // Widget Components
  const StatsCardsWidget = ({ onRemove, isEditMode }) => {
    const stats = [
      {
        title: 'Today\'s Sales',
        value: `₱${parseFloat(dashboardData.todaysSales || 0).toLocaleString()}`,
        icon: <PointOfSale sx={{ fontSize: 40 }} />,
        color: '#00B97D',
        growth: dashboardData.businessFigures?.total_sales_growth || '0',
      },
      {
        title: 'Total Transactions',
        value: dashboardData.businessFigures?.transactions || 0,
        icon: <Receipt sx={{ fontSize: 40 }} />,
        color: '#2196F3',
        growth: dashboardData.businessFigures?.transaction_growth || '0',
      },
      {
        title: 'Customers Served',
        value: dashboardData.customersServed,
        icon: <TrendingUp sx={{ fontSize: 40 }} />,
        color: '#9C27B0',
      },
      {
        title: 'Cancellations',
        value: dashboardData.businessFigures?.cancellations || 0,
        icon: <Warning sx={{ fontSize: 40 }} />,
        color: '#F44336',
        growth: dashboardData.businessFigures?.cancellation_growth || '0',
      },
    ];

    return (
      <Paper sx={{ 
        p: 3, 
        position: 'relative',
        border: isEditMode ? '2px dashed #1976d2' : 'none',
        '&:hover': isEditMode ? { 
          border: '2px dashed #1565c0',
          boxShadow: 2
        } : {}
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Business Statistics</Typography>
          {isEditMode && (
            <Box>
              <IconButton size="small" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
                <DragIcon />
              </IconButton>
              <IconButton size="small" onClick={() => onRemove(WIDGET_TYPES.STATS_CARDS)} color="error">
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </Box>
        <Grid container spacing={3}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ height: '100%', minHeight: 120 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: stat.color, mr: 2 }}>
                      {stat.icon}
                    </Box>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="h5" component="div" fontWeight="bold">
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.title}
                      </Typography>
                      {stat.growth && (
                        <Typography 
                          variant="caption" 
                          color={parseFloat(stat.growth) >= 0 ? 'success.main' : 'error.main'}
                        >
                          {parseFloat(stat.growth) >= 0 ? '+' : ''}{stat.growth}% vs last week
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  };

  const ChartWidget = ({ type, title, data, options, onRemove, isEditMode }) => (
    <Paper sx={{ 
      p: 3, 
      height: 400, 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative',
      border: isEditMode ? '2px dashed #1976d2' : 'none',
      '&:hover': isEditMode ? { 
        border: '2px dashed #1565c0',
        boxShadow: 2
      } : {}
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {isEditMode && (
          <Box>
            <IconButton size="small" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <DragIcon />
            </IconButton>
            <IconButton size="small" onClick={() => onRemove(type)} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      {data.labels && data.labels.length > 0 ? (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Pie data={data} options={options} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}
    </Paper>
  );

  const TableWidget = ({ type, title, data, columns, onRemove, maxHeight = 400, isEditMode }) => (
    <Paper sx={{ 
      p: 3, 
      height: maxHeight, 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative',
      border: isEditMode ? '2px dashed #1976d2' : 'none',
      '&:hover': isEditMode ? { 
        border: '2px dashed #1565c0',
        boxShadow: 2
      } : {}
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {isEditMode && (
          <Box>
            <IconButton size="small" sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}>
              <DragIcon />
            </IconButton>
            <IconButton size="small" onClick={() => onRemove(type)} color="error">
              <CloseIcon />
            </IconButton>
          </Box>
        )}
      </Box>
      {data.length > 0 ? (
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell key={index} sx={{ fontWeight: 'bold' }} align={column.align || 'left'}>
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.slice(0, 10).map((row, index) => (
                <TableRow key={index} hover>
                  {columns.map((column, colIndex) => (
                    <TableCell key={colIndex} align={column.align || 'left'}>
                      {column.render ? column.render(row, index) : row[column.field]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}
    </Paper>
  );

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Get current date range (today)
      const today = new Date().toISOString().split('T')[0];
      const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      // Load multiple dashboard data in parallel
      const [
        salesResponse,
        businessFiguresResponse,
        salesByCategoryResponse,
        salesByPaymentMethodResponse,
        topProductsResponse,
        fastMovingProductsResponse,
        customersServedResponse,
        salesByStaffResponse,
        recentTransactionsResponse,
        recentMovementsResponse,
        stockWarningsResponse,
      ] = await Promise.all([
        dashboardAPI.posSalesToday(),
        dashboardAPI.businessFigures(weekAgo, today),
        dashboardAPI.posSalesByCategoryToday(),
        dashboardAPI.posSalesByPaymentMethodToday(),
        dashboardAPI.posTopProductsToday(),
        dashboardAPI.fastMovingProducts(weekAgo, today),
        dashboardAPI.posCustomersServed(),
        dashboardAPI.posSalesByStaffToday(),
        dashboardAPI.recentTransactions(),
        dashboardAPI.recentProductMovements(),
        dashboardAPI.posProductsStockWarning(),
      ]);

      setDashboardData({
        todaysSales: apiUtils.formatResponse(salesResponse)?.[0]?.current_sales || 0,
        businessFigures: apiUtils.formatResponse(businessFiguresResponse) || {},
        salesByCategory: apiUtils.formatResponse(salesByCategoryResponse) || [],
        salesByPaymentMethod: apiUtils.formatResponse(salesByPaymentMethodResponse) || [],
        topProducts: apiUtils.formatResponse(topProductsResponse) || [],
        fastMovingProducts: apiUtils.formatResponse(fastMovingProductsResponse) || [],
        customersServed: apiUtils.formatResponse(customersServedResponse)?.[0]?.served || 0,
        salesByStaff: apiUtils.formatResponse(salesByStaffResponse) || [],
        recentTransactions: apiUtils.formatResponse(recentTransactionsResponse) || [],
        recentMovements: apiUtils.formatResponse(recentMovementsResponse) || [],
        stockWarnings: apiUtils.formatResponse(stockWarningsResponse) || [],
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError(apiUtils.handleError(error));
    } finally {
      setLoading(false);
    }
  };

  // Generate colors for the pie chart
  const generateColors = (count) => {
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
    ];
    return colors.slice(0, count);
  };

  // Prepare chart data
  const pieChartData = {
    labels: dashboardData.salesByCategory.map(item => item.category),
    datasets: [
      {
        data: dashboardData.salesByCategory.map(item => parseFloat(item.current_sales)),
        backgroundColor: generateColors(dashboardData.salesByCategory.length),
        borderWidth: 2,
      },
    ],
  };

  const paymentMethodChartData = {
    labels: dashboardData.salesByPaymentMethod.map(item => item.payment_type),
    datasets: [
      {
        data: dashboardData.salesByPaymentMethod.map(item => parseFloat(item.current_sales)),
        backgroundColor: generateColors(dashboardData.salesByPaymentMethod.length),
        borderWidth: 2,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Today\'s Sales by Category',
      },
    },
  };

  const paymentMethodChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Today\'s Sales by Payment Method',
      },
    },
  };

  // Widget rendering function
  const renderWidget = (widget) => {
    switch (widget.id) {
      case WIDGET_TYPES.STATS_CARDS:
        return <StatsCardsWidget onRemove={handleRemoveWidget} isEditMode={isEditMode} />;
      
      case WIDGET_TYPES.SALES_BY_CATEGORY:
        return (
          <ChartWidget
            type={WIDGET_TYPES.SALES_BY_CATEGORY}
            title="Sales by Category"
            data={pieChartData}
            options={pieChartOptions}
            onRemove={handleRemoveWidget}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.SALES_BY_PAYMENT:
        return (
          <ChartWidget
            type={WIDGET_TYPES.SALES_BY_PAYMENT}
            title="Sales by Payment Method"
            data={paymentMethodChartData}
            options={paymentMethodChartOptions}
            onRemove={handleRemoveWidget}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.TOP_PRODUCTS:
        return (
          <TableWidget
            type={WIDGET_TYPES.TOP_PRODUCTS}
            title="Top Products Today"
            data={dashboardData.topProducts}
            columns={[
              { header: 'Product', field: 'product' },
              { 
                header: 'Sales', 
                field: 'amount',
                align: 'right',
                render: (row) => `₱${parseFloat(row.amount || 0).toLocaleString()}`
              },
              { 
                header: 'Qty Sold', 
                field: 'qty',
                align: 'right'
              },
            ]}
            onRemove={handleRemoveWidget}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.STAFF_PERFORMANCE:
        return (
          <TableWidget
            type={WIDGET_TYPES.STAFF_PERFORMANCE}
            title="Staff Performance Today"
            data={dashboardData.staffPerformance}
            columns={[
              { header: 'Staff', field: 'staff_name' },
              { 
                header: 'Sales', 
                field: 'total_sales',
                align: 'right',
                render: (row) => `₱${parseFloat(row.total_sales || 0).toLocaleString()}`
              },
              { 
                header: 'Transactions', 
                field: 'transaction_count',
                align: 'right'
              },
            ]}
            onRemove={handleRemoveWidget}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.STOCK_WARNINGS:
        return (
          <TableWidget
            type={WIDGET_TYPES.STOCK_WARNINGS}
            title="Stock Warnings"
            data={dashboardData.stockWarnings}
            columns={[
              { header: 'Product', field: 'name' },
              { 
                header: 'Current Stock', 
                field: 'on_hand',
                align: 'right'
              },
              { 
                header: 'Min Stock', 
                field: 'warning_stock',
                align: 'right'
              },
            ]}
            onRemove={handleRemoveWidget}
            maxHeight={350}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.RECENT_TRANSACTIONS:
        return (
          <TableWidget
            type={WIDGET_TYPES.RECENT_TRANSACTIONS}
            title="Recent Transactions"
            data={dashboardData.recentTransactions}
            columns={[
              { 
          header: 'Txn No',
          field: 'txn_no',
          render: (row) => {
            const txnStr = String(row.txn_no || '');
            const last6 = txnStr.slice(-6);
            return `...${last6}`;
          }
              },
              { 
          header: 'Amount', 
          field: 'amount',
          align: 'right',
          render: (row) => `₱${parseFloat(row.amount || 0).toLocaleString()}`
              },
              { header: 'Customer', field: 'display_name', render: (row) => row.display_name || 'Walk-in' },
              { 
          header: 'Date', 
          field: 'trans_date',
          // render: (row) => new Date(row.trans_date).toLocaleTimeString()
              },
            ]}
            onRemove={handleRemoveWidget}
            isEditMode={isEditMode}
          />
        );
      
      case WIDGET_TYPES.RECENT_MOVEMENTS:
        return (
          <TableWidget
            type={WIDGET_TYPES.RECENT_MOVEMENTS}
            title="Recent Product Movements"
            data={dashboardData.recentMovements}
            columns={[
              { header: 'Product', field: 'name' },
              { header: 'Type', field: 'movement_type' },
              { 
                header: 'Qty', 
                field: 'qty',
                align: 'right'
              },
              { 
                header: 'Date', 
                field: 'movement_date',
                // render: (row) => new Date(row.movement_date).toLocaleTimeString()
              },
            ]}
            onRemove={handleRemoveWidget}
            maxHeight={350}
            isEditMode={isEditMode}
          />
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      overflow: 'auto',
      p: { xs: 2, sm: 3 },
      boxSizing: 'border-box'
    }}>
      {/* Header with settings */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isEditMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setAddWidgetAnchor(e.currentTarget)}
                disabled={availableWidgets.length === 0}
              >
                Add Widget
              </Button>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setSettingsOpen(true)}
              >
                Settings
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={() => setIsEditMode(false)}
                color="primary"
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setIsEditMode(true)}
            >
              Edit Dashboard
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isEditMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Edit Mode:</strong> You can now drag widgets to reorder them, add new widgets, or remove existing ones. Click "Save" when you're done customizing.
          </Typography>
        </Alert>
      )}

      {/* Customizable Dashboard with Drag and Drop */}
      {isEditMode ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="dashboard">
            {(provided, snapshot) => {
              // Find drop target index if dragging over
              let dropIndex = null;
              if (
                snapshot.isDraggingOver &&
                snapshot.draggingOverWith &&
                snapshot.draggingFromThisWith &&
                provided.placeholder &&
                provided.placeholder.props &&
                typeof provided.placeholder.props.children === 'object' &&
                provided.placeholder.props.children &&
                provided.placeholder.props.children.props &&
                typeof provided.placeholder.props.children.props.index === 'number'
              ) {
                dropIndex = provided.placeholder.props.children.props.index;
              } else if (
                snapshot.isDraggingOver &&
                provided.placeholder &&
                provided.placeholder.props &&
                typeof provided.placeholder.props.index === 'number'
              ) {
                dropIndex = provided.placeholder.props.index;
              } else if (
                snapshot.isDraggingOver &&
                provided.placeholder &&
                provided.placeholder.props &&
                typeof provided.placeholder.props.children === 'number'
              ) {
                dropIndex = provided.placeholder.props.children;
              }

              // Fallback: use provided.placeholder.props.index if available
              if (
                snapshot.isDraggingOver &&
                dropIndex === null &&
                provided.placeholder &&
                provided.placeholder.props &&
                typeof provided.placeholder.props.index === 'number'
              ) {
                dropIndex = provided.placeholder.props.index;
              }

              return (
                <Grid
                  container
                  spacing={3}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {widgets.map((widget, index) => {
                    // Insert custom placeholder at dropIndex if dragging over
                    if (
                      snapshot.isDraggingOver &&
                      dropIndex === index
                    ) {
                      return (
                        <React.Fragment key={`placeholder-${index}`}>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              border: '2px dashed #1976d2',
                              backgroundColor: 'rgba(25, 118, 210, 0.1)',
                              minHeight: 100,
                              transition: 'all 0.2s ease'
                            }}
                          />
                          <Draggable
                            key={widget.id}
                            draggableId={widget.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <Grid
                                item
                                xs={widget.size.xs}
                                sm={widget.size.sm}
                                md={widget.size.md}
                                lg={widget.size.lg}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                sx={{
                                  transition: 'transform 0.2s ease',
                                  transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                                  zIndex: snapshot.isDragging ? 1000 : 'auto',
                                }}
                              >
                                {renderWidget(widget)}
                              </Grid>
                            )}
                          </Draggable>
                        </React.Fragment>
                      );
                    }
                    return (
                      <Draggable
                        key={widget.id}
                        draggableId={widget.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Grid
                            item
                            xs={widget.size.xs}
                            sm={widget.size.sm}
                            md={widget.size.md}
                            lg={widget.size.lg}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              transition: 'transform 0.2s ease',
                              transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                              zIndex: snapshot.isDragging ? 1000 : 'auto',
                            }}
                          >
                            {renderWidget(widget)}
                          </Grid>
                        )}
                      </Draggable>
                    );
                  })}
                  {/* If dropping at the end, show placeholder after all widgets */}
                  {snapshot.isDraggingOver && dropIndex === widgets.length && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        border: '2px dashed #1976d2',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)',
                        minHeight: 100,
                        transition: 'all 0.2s ease'
                      }}
                    />
                  )}
                  {provided.placeholder}
                </Grid>
              );
            }}
          </Droppable>
        </DragDropContext>
      ) : (
        <Grid container spacing={3}>
          {widgets.map((widget) => (
            <Grid 
              key={widget.id}
              item 
              xs={widget.size.xs} 
              sm={widget.size.sm} 
              md={widget.size.md} 
              lg={widget.size.lg}
            >
              {renderWidget(widget)}
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add Widget Menu */}
      <Menu
        anchorEl={addWidgetAnchor}
        open={Boolean(addWidgetAnchor)}
        onClose={() => setAddWidgetAnchor(null)}
      >
        {availableWidgets.map((widget) => (
          <MenuItem 
            key={widget.id} 
            onClick={() => handleAddWidget(widget)}
            sx={{ minWidth: 200 }}
          >
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {widget.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {widget.description}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Settings Dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dashboard Settings</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize your dashboard by adding or removing widgets. You can also drag and drop widgets to reorder them.
          </Typography>
          
          <Typography variant="h6" sx={{ mb: 1 }}>Active Widgets</Typography>
          <Box sx={{ mb: 3 }}>
            {widgets.map((widget) => (
              <Chip
                key={widget.id}
                label={widget.title}
                onDelete={() => handleRemoveWidget(widget.id)}
                sx={{ m: 0.5 }}
              />
            ))}
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>Available Widgets</Typography>
          <Box sx={{ mb: 2 }}>
            {availableWidgets.map((widget) => (
              <Chip
                key={widget.id}
                label={widget.title}
                onClick={() => handleAddWidget(widget)}
                sx={{ m: 0.5 }}
                variant="outlined"
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetLayout} color="warning">
            Reset to Default
          </Button>
          <Button onClick={() => {
            setSettingsOpen(false);
            setIsEditMode(false);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
